import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Mail, Globe, Send, Sparkles, ChevronRight, Palette, Users, BarChart3
} from "lucide-react";

// Landing Page - Public marketing page
const LandingPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-afa-navy rounded-xl flex items-center justify-center">
              <Mail className="w-5 h-5 text-white" />
            </div>
            <div className="flex flex-col">
              <span className="text-base font-bold text-slate-900 leading-tight" style={{fontFamily: 'Manrope'}}>Simply Smarter</span>
              <span className="text-sm font-medium text-afa-teal leading-tight">Email</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/login')}
              className="text-slate-700 font-medium hover:text-afa-navy transition-colors"
            >
              Sign In
            </button>
            <Button
              onClick={() => navigate('/login')}
              className="bg-afa-lime text-afa-navy hover:bg-afa-lime/90 rounded-full px-5 font-semibold"
            >
              Get Started Free
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section - Compact */}
      <section className="pt-16 relative overflow-hidden">
        <div className="bg-gradient-to-br from-[#003858] via-[#006080] to-[#119AB3] px-6 py-16 md:py-24 relative">
          {/* Decorative elements */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#B7EB1B]/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-[#119AB3]/30 rounded-full blur-3xl"></div>
          </div>

          {/* Badge */}
          <div className="relative z-10 text-center mb-6">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-5 py-2 border border-white/20">
              <Sparkles className="w-4 h-4 text-afa-lime" />
              <span className="text-white/90 text-sm font-medium">AI newsletters for small business</span>
            </div>
          </div>

          {/* Main Heading */}
          <div className="relative z-10 text-center max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-6xl font-bold mb-4" style={{fontFamily: 'Manrope'}}>
              <span className="text-white">Beautiful emails. </span>
              <span className="text-afa-lime">In 60 seconds.</span>
            </h1>
            <p className="text-white/80 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed mb-8">
              We've spent 30 years helping businesses grow. Now we're packaging that know-how into simple tools you can use yourself — starting with one ridiculously simple newsletter tool.
            </p>

            {/* CTA Button */}
            <Button
              onClick={() => navigate('/login')}
              className="bg-afa-lime text-afa-navy hover:bg-afa-lime/90 rounded-full px-8 py-6 text-lg font-semibold shadow-lg shadow-black/20 hover:shadow-xl transition-all hover:scale-105"
            >
              Get Started Free <ChevronRight className="w-5 h-5 ml-1" />
            </Button>
          </div>
        </div>
      </section>

      {/* 3 Simple Steps - Visual & Prominent */}
      <section className="py-16 px-6 bg-white relative">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-12">
            <p className="text-afa-teal font-semibold text-sm uppercase tracking-wider mb-2">How It Works</p>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>
              3 Simple Steps
            </h2>
          </div>

          {/* Steps - Horizontal Visual Flow */}
          <div className="relative">
            {/* Connection Line */}
            <div className="hidden md:block absolute top-24 left-[20%] right-[20%] h-1 bg-gradient-to-r from-afa-navy via-afa-teal to-afa-lime rounded-full"></div>

            <div className="grid md:grid-cols-3 gap-8 md:gap-12">
              {/* Step 1 */}
              <div className="text-center relative">
                <div className="w-20 h-20 bg-gradient-to-br from-afa-navy to-afa-teal rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-afa-navy/20 relative z-10">
                  <Globe className="w-9 h-9 text-white" />
                </div>
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-2 w-8 h-8 bg-afa-lime rounded-full flex items-center justify-center text-afa-navy font-bold text-sm shadow-md">
                  1
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2" style={{fontFamily: 'Manrope'}}>Paste Your URL</h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Copy any blog post or webpage. AI extracts content & images automatically.
                </p>
              </div>

              {/* Step 2 */}
              <div className="text-center relative">
                <div className="w-20 h-20 bg-gradient-to-br from-afa-teal to-afa-navy rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-afa-teal/20 relative z-10">
                  <Palette className="w-9 h-9 text-white" />
                </div>
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-2 w-8 h-8 bg-afa-lime rounded-full flex items-center justify-center text-afa-navy font-bold text-sm shadow-md">
                  2
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2" style={{fontFamily: 'Manrope'}}>Design Your Look</h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Pick a theme, adjust colors, edit text. See changes in real-time.
                </p>
              </div>

              {/* Step 3 */}
              <div className="text-center relative">
                <div className="w-20 h-20 bg-gradient-to-br from-afa-lime to-afa-teal rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-afa-lime/20 relative z-10">
                  <Send className="w-9 h-9 text-afa-navy" />
                </div>
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-2 w-8 h-8 bg-afa-lime rounded-full flex items-center justify-center text-afa-navy font-bold text-sm shadow-md">
                  3
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2" style={{fontFamily: 'Manrope'}}>Send or Schedule</h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Upload contacts, test it, then send now or schedule for later.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features - Compact Grid */}
      <section className="py-16 px-6 bg-slate-50">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: Sparkles, title: "AI-Powered", desc: "Auto-generates subject lines & content" },
              { icon: Palette, title: "13+ Themes", desc: "Industry-specific beautiful designs" },
              { icon: Users, title: "Contact Manager", desc: "Import once, reuse everywhere" },
              { icon: BarChart3, title: "Analytics", desc: "Track opens, clicks & more" },
            ].map((feature, i) => (
              <div key={i} className="bg-white rounded-xl p-5 border border-slate-100 hover:shadow-md transition-all">
                <feature.icon className="w-8 h-8 text-afa-teal mb-3" />
                <h3 className="font-bold text-slate-900 mb-1" style={{fontFamily: 'Manrope'}}>{feature.title}</h3>
                <p className="text-slate-500 text-sm">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-[#003858] to-[#119AB3]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{fontFamily: 'Manrope'}}>
            Ready to send smarter newsletters?
          </h2>
          <p className="text-white/80 text-lg mb-8">
            Join the small businesses saving hours every week with AI-powered email design.
          </p>
          <Button
            onClick={() => navigate('/login')}
            className="bg-afa-lime text-afa-navy hover:bg-afa-lime/90 rounded-full px-10 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all hover:scale-105"
          >
            Start Creating Now <ChevronRight className="w-5 h-5 ml-1" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 px-6 bg-white border-t border-slate-100">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-slate-500 text-sm">
            <Mail className="w-4 h-4" />
            <span>Simply Smarter Email · Built by Anne Flinn Advertising</span>
          </div>
          <p className="text-slate-400 text-sm">© 2024 All rights reserved</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
